// =============================================================================
// FILE: components/hover/HoverIntelligenceProvider.tsx
// PURPOSE: R2 Smart Hover Intelligence — global event listener that detects
//   hovered elements and shows a contextual action palette after the configured
//   delay. Elements opt in via data-r2-hover="type" attribute.
//   Optional context via data-r2-context='{"title":"...", "description":"..."}'
// =============================================================================

import { useEffect, useRef, useState, useCallback, type JSX } from 'react';
import { HoverPalette } from './HoverPalette.js';
import { useHoverIntelligence } from '../../stores/hoverIntelligence.store.js';
import type { HoverElementType } from '../../stores/hoverIntelligence.store.js';

interface ActiveHover {
  type: HoverElementType;
  context?: { title?: string; description?: string };
  position: { x: number; y: number };
}

export function HoverIntelligenceProvider(): JSX.Element | null {
  const { enabled, enabledTypes, maxActions, showAIBadge } = useHoverIntelligence();
  const [active, setActive] = useState<ActiveHover | null>(null);
  const timerRef    = useRef<ReturnType<typeof setTimeout> | null>(null);
  const currentElRef = useRef<Element | null>(null);

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const findHoverTarget = useCallback((el: Element | null): Element | null => {
    if (!el || el === document.body) return null;
    if ((el as HTMLElement).dataset.r2Hover) return el;
    return findHoverTarget(el.parentElement);
  }, []);

  useEffect(() => {
    if (!enabled) {
      setActive(null);
      return;
    }

    const onMove = (e: MouseEvent) => {
      const target = findHoverTarget(e.target as Element);

      if (!target) {
        // Mouse moved off a hover element — start dismissal
        if (currentElRef.current) {
          clearTimer();
          currentElRef.current = null;
          // Small grace period before hiding so user can move to palette
          timerRef.current = setTimeout(() => {
            setActive(null);
          }, 200);
        }
        return;
      }

      if (target === currentElRef.current) return; // same element, no-op

      clearTimer();
      setActive(null);
      currentElRef.current = target;

      const rawType = (target as HTMLElement).dataset.r2Hover as HoverElementType;
      if (!enabledTypes.includes(rawType)) return;

      // Parse optional context
      let context: ActiveHover['context'];
      const rawCtx = (target as HTMLElement).dataset.r2Context;
      if (rawCtx) {
        try { context = JSON.parse(rawCtx); } catch { /* ignore */ }
      }

      timerRef.current = setTimeout(() => {
        setActive({
          type: rawType,
          context,
          position: { x: e.clientX, y: e.clientY },
        });
      }, 3000);
    };

    // When palette itself is hovered, cancel dismissal
    const onPaletteEnter = () => {
      clearTimer();
    };

    document.addEventListener('mousemove', onMove, { passive: true });
    return () => {
      document.removeEventListener('mousemove', onMove);
      clearTimer();
    };
  }, [enabled, enabledTypes, clearTimer, findHoverTarget]);

  const handleAction = useCallback((actionId: string, type: HoverElementType) => {
    // Dispatch a custom event so any part of the app can react
    window.dispatchEvent(new CustomEvent('r2:hover:action', {
      detail: { actionId, type, context: active?.context },
    }));

    // Built-in handlers for simple actions
    if (actionId === 'copy' || actionId === 'copy-ref') {
      const el = currentElRef.current;
      if (el) {
        const text = el.textContent ?? '';
        void navigator.clipboard.writeText(text.trim());
      }
    }

    setActive(null);
    currentElRef.current = null;
  }, [active]);

  const handleClose = useCallback(() => {
    setActive(null);
    currentElRef.current = null;
    clearTimer();
  }, [clearTimer]);

  if (!active) return null;

  return (
    <HoverPalette
      elementType={active.type}
      context={active.context}
      position={active.position}
      maxActions={maxActions}
      showAIBadge={showAIBadge}
      onAction={handleAction}
      onClose={handleClose}
    />
  );
}
